import { useEffect, useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { ShopifyCampaignCoupon, ShopifyCouponState } from '@discovr/features/shopify';
import { shopifyCopy } from '@discovr/features/copy';
import { SimpleTooltip } from '../../components/ui/tooltip';
import { TruncatedText } from '../../components/common/TruncatedText';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { ScreenReaderLabel } from '../../components/common/ScreenReaderLabel';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import { COUPON_NAME_MAX } from './shopifyCodeUtils';
import { statusLabel } from './parts/statusMap';

/**
 * Presentational Shopify coupon pieces shared across both the authed and the
 * anonymous surfaces. Current importers: ShopifySettingsTab, ShopifySettingsTab's
 * modals (ShopifyConfigEditModalBody, ShopifyReplaceModalBody),
 * ShopifyCouponConfigForm, PublicShopifyCouponsTab, and
 * campaign-reporting/ShopifyConversionsTab.
 *
 * RULE: nothing in this file may import a mutation hook, a modal, or
 * ShopifySettingsTab. It lands in the anonymous public share-view chunk, and
 * Vite/Rollup can't split a module that two entry points import statically —
 * so one such import ships the whole mutation surface (Generate, replace,
 * config-edit, invalidate, bulk-invalidate) to unauthenticated visitors,
 * unreachable but downloaded. Nothing enforces this but this comment: the repo
 * has no no-restricted-imports or dependency-cruiser rule, and the only signal
 * is a Rollup warning in `pnpm turbo build` output.
 */

/** Maps BE coupon status values to display label, Badge variant and an
 *  optional hint. The ONE status vocabulary: the Coupons table, the public
 *  share view and the Conversions report all read it through
 *  getCouponStatus, so a label can never mean two things on two screens. */
export const COUPON_STATUS_DISPLAY: Record<
  string,
  { label: string; variant: 'secondary' | 'outline' | 'default'; hint?: string }
> = {
  // "Active", not "Assigned": `assigned` is the BE's word for "live on
  // Shopify", and a brand reads this table in active-vs-inactive terms. The
  // wire value is untouched. Both the Coupons rows and the Conversions STATUS
  // column read this map, so the two can't drift apart.
  assigned: { label: 'Active', variant: 'default' },
  // "Replaced", not "Unused": an orphaned coupon is one a Replace superseded,
  // and it keeps the orders it already earned — "Unused" called a code with
  // real sales against it unused (item 125). Usage belongs in the Orders
  // column, never in the status.
  orphaned: {
    label: 'Replaced',
    variant: 'secondary',
    hint: 'Replaced by a newer code. Past orders still count.',
  },
  // "Inactive", matching the section that holds them ("Inactive codes") and
  // the toast that creates them ("Deactivated N codes").
  invalid: { label: 'Inactive', variant: 'outline' },
  deleted: { label: 'Deleted', variant: 'outline' },
};

/** True for a name the server would reject (discovr#596). Only a name saved
 *  before the cap existed can be over it — the inputs carry `maxLength`, and
 *  nothing auto-trims: silently renaming a brand's live coupon would be worse
 *  than refusing the save and saying why. */
export const isCouponNameOverCap = (value: string) => value.length > COUPON_NAME_MAX;

export const COUPON_NAME_OVER_CAP_MESSAGE = `Shorten to ${COUPON_NAME_MAX} characters to save changes.`;

/** The same instruction with the field named, for the toast a blocked save
 *  fires. A toast is read out of context and dismisses itself, so the bare
 *  "Shorten to 20…" leaves a screen-reader user holding an instruction with no
 *  target. Derived, not retyped, so the two can't drift. */
export const COUPON_NAME_OVER_CAP_TOAST = `Discount code name — ${COUPON_NAME_OVER_CAP_MESSAGE}`;

/** Gap between clearing the copy live region and re-setting it (item 119).
 *  Long enough that a screen reader treats the two as separate mutations,
 *  short enough that the confirmation still reads as immediate. */
const REANNOUNCE_DELAY_MS = 150;

const DETACHED_REGION_ID = 'shopify-copy-announcer';

/** How long the detached region keeps its text once announced. Long enough for
 *  a reader to pick the message up, short enough that no stale "copied" status
 *  lingers in the page afterwards. */
const DETACHED_CLEAR_MS = 1500;

// Module-level because the detached region is shared: a newer announcement
// must cancel the older one's pending set/clear, not race it.
let detachedSetTimer: ReturnType<typeof setTimeout> | undefined;
let detachedClearTimer: ReturnType<typeof setTimeout> | undefined;

/** Hands a pending copy announcement to a region that outlives the cell, so a
 *  row that unmounts inside the re-announce gap doesn't lose "copied". The
 *  region is transient: after announcing it is emptied and removed, so no
 *  global live region (or its stale text) outlives the announcement. */
function announceDetached(message: string) {
  clearTimeout(detachedSetTimer);
  clearTimeout(detachedClearTimer);
  let region = document.getElementById(DETACHED_REGION_ID);
  if (!region) {
    region = document.createElement('div');
    region.id = DETACHED_REGION_ID;
    region.setAttribute('role', 'status');
    region.className = 'sr-only';
    document.body.appendChild(region);
  }
  const live = region;
  live.textContent = '';
  detachedSetTimer = setTimeout(() => {
    live.textContent = message;
    detachedClearTimer = setTimeout(() => {
      live.textContent = '';
      live.remove();
    }, DETACHED_CLEAR_MS);
  }, REANNOUNCE_DELAY_MS);
}

/** Character counter for the coupon-name fields, which share a hard cap.
 *  maxLength blocks the keystroke silently, so the limit is also announced
 *  (same shape as HoldNoteField). Over the cap it adds the visible reason the
 *  save is blocked; `messageId` is what the field's own aria-describedby has
 *  to point at, since only the caller owns the <Input>. Not announced through
 *  the live region as well — the field is invalid from first render, which an
 *  alert fires too early to catch, so aria-describedby (read on focus, i.e.
 *  when the brand goes to fix it) is the mechanism that actually works. */
export function CouponNameCounter({ value, messageId }: { value: string; messageId: string }) {
  const over = isCouponNameOverCap(value);
  const atLimit = value.length >= COUPON_NAME_MAX;
  return (
    <>
      <span
        aria-hidden="true"
        className={cn(
          'text-caption-regular mt-1 block text-right tabular-nums',
          over ? 'text-destructive' : 'text-muted-foreground',
        )}
      >
        {value.length}/{COUPON_NAME_MAX}
      </span>
      {over && (
        <p id={messageId} className="text-destructive text-caption-regular">
          {COUPON_NAME_OVER_CAP_MESSAGE}
        </p>
      )}
      <span aria-live="polite" className="sr-only">
        {atLimit && !over ? `Discount code name is at the ${COUPON_NAME_MAX} character limit` : ''}
      </span>
    </>
  );
}

/** Click-to-copy text where the value itself is the trigger, with the full
 *  value on hover.
 *
 *  The tooltip is NOT gated on measured truncation (TruncationTooltip /
 *  useIsTruncated), which is how this started out. With the gate, prod showed
 *  no tooltip at all on the 131-character Gemini codes, while TruncatedText's
 *  own tooltip on a long creator name two tables over worked fine — so the
 *  gate is not broken in table cells generally. The difference is `w-fit`
 *  below: a fit-content box sizes to its text, so whether it clips at all
 *  rests entirely on `max-w-full` clamping it, and that is what fails here.
 *  TruncatedText is a plain block whose auto width fills the cell, so it clips
 *  either way and measures true. `w-fit` is not negotiable on this chip (it
 *  was `w-full` once; the full-width grey bar was rejected in review), so the
 *  measurement cannot be made reliable here — hence no gate at all. Cost:
 *  hovering a short code repeats it, which the owner accepted. */
export function CopyOnClickText({
  value,
  kind,
  className,
}: {
  value: string;
  kind: 'code' | 'link';
  className?: string;
}) {
  const [announced, setAnnounced] = useState('');
  const copyLabelId = useId();
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pendingRef = useRef<string | undefined>(undefined);

  // Copying the same value twice writes the same string into the live region,
  // and an unchanged region is not an update, so the reader says nothing the
  // second time (item 119). Clearing it and re-setting it makes every copy a
  // change. The gap is a timeout rather than one animation frame: ~16ms is
  // inside the window where screen readers coalesce successive mutations on
  // one node, which would collapse the clear and the set back into no change.
  const announce = (message: string) => {
    if (timerRef.current !== undefined) clearTimeout(timerRef.current);
    setAnnounced('');
    pendingRef.current = message;
    timerRef.current = setTimeout(() => {
      timerRef.current = undefined;
      pendingRef.current = undefined;
      setAnnounced(message);
    }, REANNOUNCE_DELAY_MS);
  };

  useEffect(
    () => () => {
      if (timerRef.current === undefined) return;
      clearTimeout(timerRef.current);
      if (pendingRef.current !== undefined) announceDetached(pendingRef.current);
    },
    [],
  );

  const copy = async () => {
    const noun = kind === 'code' ? 'Code' : 'Link';
    try {
      await navigator.clipboard.writeText(value);
      // Visual only: the Toaster's live region would repeat what the status
      // below already says, and that status survives a modal hiding the toast.
      toast.success(<span aria-hidden="true">{`${noun} copied`}</span>);
      announce(`${noun} copied`);
    } catch {
      // The value goes in the toast too: the button text is truncated, so
      // "copy it manually" is not actionable from the cell alone. Only the
      // message is hidden from AT (the status says it); the value stays readable.
      toast.error(
        <span aria-hidden="true">{`Couldn't copy the ${kind}. Copy it manually.`}</span>,
        {
          description: value,
        },
      );
      announce(`Couldn't copy the ${kind}`);
    }
  };

  return (
    <>
      <span role="status" className="sr-only">
        {announced}
      </span>
      {/* The value is in the button's own accessible name too, so a screen reader
          hears it twice while this is open. Kept anyway: the label is what
          tells two rows apart, and the tooltip is the only way a sighted user
          reads a clipped code. */}
      <SimpleTooltip content={value}>
        <button
          type="button"
          onClick={copy}
          aria-labelledby={copyLabelId}
          className={cn(
            'focus-visible:ring-ring block w-fit max-w-full min-w-0 cursor-pointer truncate rounded-sm text-left outline-none hover:underline focus-visible:ring-[3px]',
            className,
          )}
        >
          {value}
          <ScreenReaderLabel id={copyLabelId}>
            Copy {kind} {value}
          </ScreenReaderLabel>
        </button>
      </SimpleTooltip>
    </>
  );
}

/** A discount code in any Shopify table: always one line, clipped with an
 *  ellipsis, the full code in the tooltip on hover and keyboard focus, click
 *  copies. `chip` adds the grey pill used by the Coupons rows. */
export function ShopifyCodeCell({
  code,
  chip = false,
  className,
}: {
  code: string;
  chip?: boolean;
  className?: string;
}) {
  return (
    <CopyOnClickText
      value={code}
      kind="code"
      className={cn(
        'font-mono',
        chip && 'bg-muted rounded-sm px-1.5 py-0.5 text-body-sm-medium',
        className,
      )}
    />
  );
}

/** `shopifyState` is always present on authed rows and is the only input they
 *  use. The date arguments are the fallback for the public share view alone,
 *  whose payload carries no shopify_state; there, an end date at or before now
 *  reads as expired (the backend's `!End.After(now)`).
 *
 *  A coupon whose end_date has passed is still `assigned` in the BE (Shopify
 *  has no coupon-level expiry webhook, and there's deliberately no cron here
 *  to flip status over time) — Shopify itself just silently stops honoring
 *  it. This is a display-only computation so the badge doesn't mislead the
 *  brand into thinking an unusable code is still live; every other action
 *  (Delete/Invalidate/EditConfig) keeps operating on the real `assigned`
 *  status underneath, unchanged. */
export function isCouponExpired(
  status: string,
  endDate?: string | null,
  shopifyState?: ShopifyCouponState,
): boolean {
  if (status !== 'assigned') return false;
  if (shopifyState) return shopifyState === 'expired';
  if (!endDate) return false;
  return new Date(endDate).getTime() <= Date.now();
}

function isCouponScheduled(
  status: string,
  startDate?: string | null,
  shopifyState?: ShopifyCouponState,
): boolean {
  if (status !== 'assigned') return false;
  if (shopifyState) return shopifyState === 'scheduled';
  return !!startDate && new Date(startDate).getTime() > Date.now();
}

export function getCouponStatus(
  status: string,
  endDate?: string | null,
  startDate?: string | null,
  shopifyState?: ShopifyCouponState,
  usageLimitReached?: boolean,
): { label: string; variant: 'secondary' | 'outline' | 'default'; hint?: string } {
  // Dates are synced from Shopify, where turning a discount off sets its end
  // date to now — so "Expired" also covers a code deactivated in Shopify.
  if (isCouponExpired(status, endDate, shopifyState)) {
    return { label: 'Expired', variant: 'outline', hint: 'Ended, or deactivated in Shopify.' };
  }
  if (isCouponScheduled(status, startDate, shopifyState)) {
    return { label: 'Scheduled', variant: 'secondary', hint: 'Starts working on its start date.' };
  }
  // Shopify still reads Active, but checkout refuses a code at its total limit.
  if (status === 'assigned' && usageLimitReached) {
    return {
      label: 'Usage limit reached',
      variant: 'outline',
      hint: 'Shopify refuses this code at checkout. Raise or remove the usage limit to use it again.',
    };
  }
  return COUPON_STATUS_DISPLAY[status] ?? { label: status, variant: 'outline' };
}

/** The status badge, so label, variant and hint travel together.
 *
 *  The hint is plain sr-only text rather than a tooltip. A tooltip would have
 *  left the meaning of "Replaced" unavailable to exactly the people most
 *  likely to need it: Radix tooltips don't open on tap, so touch and
 *  screen-magnifier users never see one, and making the badge focusable to
 *  reach it added a tab stop to every row of the table. In the DOM the hint
 *  also needs no TooltipProvider, so the public share view gets it too. */
export function CouponStatusBadge({
  status,
  endDate,
  startDate,
  shopifyState,
  usageLimitReached,
  className = 'px-1.5 py-0',
}: {
  status: string;
  endDate?: string | null;
  startDate?: string | null;
  shopifyState?: ShopifyCouponState;
  usageLimitReached?: boolean;
  className?: string;
}) {
  const { label, variant, hint } = getCouponStatus(
    status,
    endDate,
    startDate,
    shopifyState,
    usageLimitReached,
  );
  return (
    <Badge variant={variant} className={className}>
      {label}
      {hint ? <span className="sr-only"> — {hint}</span> : null}
    </Badge>
  );
}

/** Shared creator cell JSX for both active and revoked coupon rows. Shows a
 *  green "Live" chip when the creator's content is live (creator_live). */
export function CreatorCell({ c }: { c: ShopifyCampaignCoupon }) {
  if (c.creator_name || c.creator_avatar) {
    return (
      <div className="flex items-center gap-2">
        <Avatar className="size-7">
          {c.creator_avatar ? (
            <AvatarImage src={c.creator_avatar} alt="" className="object-cover" />
          ) : null}
          <AvatarFallback />
        </Avatar>
        <div className="min-w-0">
          <span className="flex items-center gap-1.5">
            <TruncatedText text={c.creator_name ?? ''} placeholder="—" className="font-medium" />
            {c.creator_live && (
              <Badge variant="outline" className="gap-1 border-emerald-200 bg-emerald-50 text-emerald-700">
                <span aria-hidden className="size-1.5 rounded-full bg-emerald-500" />
                Live
              </Badge>
            )}
          </span>
          {c.creator_handle && (
            <TruncatedText
              text={`@${c.creator_handle}`}
              className="text-muted-foreground text-caption-regular block"
            />
          )}
          {c.creator_on_hold && <CreatorHoldLine stage={c.creator_stage} />}
        </div>
      </div>
    );
  }
  if (c.scope === 'campaign') return <span className="text-muted-foreground">Campaign-wide</span>;
  return <span className="text-muted-foreground">—</span>;
}

function CreatorHoldLine({ stage }: { stage?: string }) {
  return (
    <span className="text-caption-regular mt-0.5 flex flex-wrap items-center gap-1.5">
      <Badge variant="outline" className="border-amber-200 px-1.5 py-0 text-amber-700">
        {shopifyCopy.creatorHold.badge}
      </Badge>
      {stage && <span className="text-muted-foreground">{statusLabel(stage)}</span>}
      <span className="sr-only">{shopifyCopy.creatorHold.rowHint}</span>
    </span>
  );
}

/** Shared tracking link cell — clicking copies the link; it does not open it. */
export function TrackingLinkCell({ c }: { c: ShopifyCampaignCoupon }) {
  if (!c.tracking_link) return <span className="text-muted-foreground">—</span>;
  return (
    <CopyOnClickText
      value={c.tracking_link}
      kind="link"
      className="text-muted-foreground text-caption-regular max-w-[150px] hover:underline"
    />
  );
}
